// 정책 고지 배너 — config/policyNotices.{key} 문서 값을 그대로 표시. 문서·필드가 없으면 아무것도 그리지 않는다.
// 사용처: /patient/terms(PatientTerms), /find-clinic(PublicClinicDirectory) 상단 (구 환자앱 종료 고지, 2026-09-14)
// 금지: 덴클 앱 유도 문구·혜택 문구 없음 — 문안은 Firestore 값(법적보안 확정본)만, 이 컴포넌트는 문구를 갖지 않는다.
import React, { useEffect, useState } from 'react';
import { getPolicyNotice, PATIENTAPP_SHUTDOWN_KEY } from '../../services/policyNotices';

// 문서 값은 Date(서비스가 변환), fallback 값은 '2026-09-16' 같은 문자열이 온다 — 둘 다 받는다.
// (2026-09-29 버그: fallback 문자열에 getFullYear를 불러 /as-remake 첫 렌더가 통째로 떨어져 흰 화면. 대표 신고)
const asDate = (v) => { if (!v) return null; const d = v instanceof Date ? v : new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
const fmt = (v) => { const d = asDate(v); return d ? `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일` : ''; };

// fallback: 문서·필드가 없을 때 보여 줄 { title, body, effectiveAt?, link? } (없으면 미표시). 문안은 호출하는 화면이 정본 문서에서 가져온다
// dismissible: 닫기 버튼(이 브라우저에서만 숨김, localStorage). 약관 페이지 상단 고지 박스는 이 배너와 별개로 항상 노출한다
function PolicyNoticeBanner({ noticeKey = PATIENTAPP_SHUTDOWN_KEY, style, fallback, dismissible = false, linkLabel = '전문 보기' }) {
    const [notice, setNotice] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let alive = true;
        getPolicyNotice(noticeKey).then(n => { if (alive) setNotice(n); });
        return () => { alive = false; };
    }, [noticeKey]);

    const n = notice || (fallback ? { ...fallback, title: fallback.title || '', body: fallback.body || '' } : null);
    if (!n) return null;
    // 시행일이 지나면 자동으로 내린다(고지 기록 자체는 남긴다)
    const effective = n.effectiveAt ? (n.effectiveAt instanceof Date ? n.effectiveAt : new Date(n.effectiveAt)) : null;
    if (effective && !Number.isNaN(effective.getTime()) && Date.now() > effective.getTime() + 24 * 3600 * 1000) return null;
    const dismissKey = `dc_noticeDismiss_${noticeKey}_${n.version || ''}`;
    if (dismissed || (dismissible && readDismissed(dismissKey))) return null;
    const dateLine = [
        n.endsAt ? `종료일 ${fmt(n.endsAt)}` : '',
        n.purgeAt && n.purgeDueAt ? `파기 ${fmt(n.purgeAt)} ~ ${fmt(n.purgeDueAt)}` : (n.purgeAt ? `파기 개시 ${fmt(n.purgeAt)}` : (n.purgeDueAt ? `파기 완료 기한 ${fmt(n.purgeDueAt)}` : '')),
        n.postedAt ? `게시일 ${fmt(n.postedAt)}` : '',
    ].filter(Boolean).join(' · ');
    return (
        <div role="status" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', marginBottom: 16, color: '#78350f', fontSize: 13, lineHeight: 1.6, ...(style || {}) }}>
            {n.title && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{n.title}</div>}
            {n.body && <div style={{ whiteSpace: 'pre-line' }}>{n.body}</div>}
            {dateLine && <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>{dateLine}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                {n.link && <a href={n.link} style={{ fontSize: 12, color: '#92400e', textDecoration: 'underline' }}>{linkLabel}</a>}
                {dismissible && (
                    <button type="button" onClick={() => { writeDismissed(dismissKey); setDismissed(true); }}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#92400e', textDecoration: 'underline', cursor: 'pointer' }}>
                        닫기
                    </button>
                )}
            </div>
        </div>
    );
}

// 닫기 상태는 이 브라우저에만 저장 — 실패해도 배너만 계속 보일 뿐이므로 기록만 남긴다
function readDismissed(key) {
    try { return window.localStorage.getItem(key) === '1'; }
    catch (e) { console.error('[policyNotices] 닫기 상태 읽기 실패', key, e); return false; }
}
function writeDismissed(key) {
    try { window.localStorage.setItem(key, '1'); }
    catch (e) { console.error('[policyNotices] 닫기 상태 저장 실패', key, e); }
}

export default PolicyNoticeBanner;
